import {
  SPECIAL_TYPE,
  TARGET_TYPE
} from '@/modules/dataMaintenance/constants/Special.constants'
import moment from 'moment-timezone'

export const MultiSpecialUploadHelper = {
  validateSpecialData: (special = {}) => {
    const validationMessages = []
    if (!special.selectedSpecialType) {
      validationMessages.push({
        fieldName: 'selectedSpecialType',
        message: 'Special Type is required'
      })
    }
    if (
      special.selectedSpecialType === SPECIAL_TYPE.BY_FAMILY_GROUP &&
      !special.selectedFamilyGroup
    ) {
      validationMessages.push({
        fieldName: 'selectedFamilyGroup',
        message: 'Family Group is required'
      })
    } else if (
      special.selectedSpecialType === SPECIAL_TYPE.BY_PRODUCT &&
      !special.selectedProduct
    ) {
      validationMessages.push({
        fieldName: 'selectedProduct',
        message: 'Product is required'
      })
    }
    if (!special.startDate) {
      validationMessages.push({
        fieldName: 'startDate',
        message: 'Start Date is required'
      })
    }
    if (!special.endDate) {
      validationMessages.push({
        fieldName: 'endDate',
        message: 'End Date is required'
      })
    }
    const hasValidDateRange =
      moment(special.startDate, moment.ISO_8601, true).isValid() &&
      moment(special.endDate, moment.ISO_8601, true).isValid()
    if (
      hasValidDateRange &&
      moment(special.startDate).isAfter(special.endDate)
    ) {
      validationMessages.push({
        fieldName: 'startDate',
        message: 'Start Date cannot be after the End Date'
      })
    }
    if (!special.targetType) {
      validationMessages.push({
        fieldName: 'targetType',
        message: 'Target type selection required'
      })
    }
    if (
      special.targetType === TARGET_TYPE.BANNER_GROUP &&
      (!special.targetBannerGroupIds ||
        special.targetBannerGroupIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetBannerGroupIds',
        message: 'Banner Group(s) required'
      })
    } else if (
      special.targetType === TARGET_TYPE.MULTI_STORE_OPERATOR &&
      (!special.targetMultiStoreOperatorIds ||
        special.targetMultiStoreOperatorIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetMultiStoreOperatorIds',
        message: 'Multi-Store Operator(s) required'
      })
    } else if (
      special.targetType === TARGET_TYPE.RETAILER_STORE &&
      (!special.targetStoreIds || special.targetStoreIds.length === 0)
    ) {
      validationMessages.push({
        fieldName: 'targetStoreIds',
        message: 'Retailer(s) required'
      })
    }

    return validationMessages
  },

  validateSpecialPricing: (special = {}) => {
    const validationMessages = []
    const isServiceProduct = special?.selectedProduct?.productType === 'SERVICE'

    if (!special.regionalPriceMap || special.regionalPriceMap.length === 0) {
      validationMessages.push({
        fieldName: 'zonePrices',
        message: 'Zone Price(s) required'
      })
    }

    if (special.regionalPriceMap) {
      // Find rows that have one of its fields filled
      const validatableRegionPriceMapRows = special.regionalPriceMap.filter(
        (row) => row.amountOff || (!isServiceProduct && row.unitRrp)
      )

      // Find rows that are fully filled
      const completedRegionPriceMapRows = validatableRegionPriceMapRows.filter(
        (row) => row.amountOff && (isServiceProduct || row.unitRrp)
      )

      // Rule 1. Must have at least 1 priced zone
      if (completedRegionPriceMapRows.length === 0) {
        validationMessages.push({
          fieldName: 'amountOff',
          message: 'Amount Off must be positive'
        })
        if (!isServiceProduct) {
          validationMessages.push({
            fieldName: 'unitRrp',
            message: 'Unit Rrp must be positive'
          })
        }
      }

      // Rule 2. Cannot have incompleted row fields
      if (
        validatableRegionPriceMapRows.length !==
        completedRegionPriceMapRows.length
      ) {
        validationMessages.push({
          fieldName: 'not_applicable',
          message: 'Zone Pricing is not complete'
        })
      }

      if (completedRegionPriceMapRows.length > 0) {
        completedRegionPriceMapRows.forEach((zonePrice) => {
          if (!zonePrice.amountOff || zonePrice.amountOff <= 0) {
            validationMessages.push({
              fieldName: 'amountOff',
              message: 'Amount Off must be positive'
            })
          }
          if (!zonePrice.cartonPrice || zonePrice.cartonPrice < 0) {
            validationMessages.push({
              fieldName: 'cartonPrice',
              message: 'Carton Price must be positive'
            })
          }
          if (zonePrice.amountOff > zonePrice.cartonPrice) {
            validationMessages.push({
              fieldName: 'amountOff',
              message: 'Amount Off must be lower than Carton Price'
            })
          }
          if (!isServiceProduct) {
            if (!zonePrice.unitRrp || zonePrice.unitRrp <= 0) {
              validationMessages.push({
                fieldName: 'unitRrp',
                message: 'Unit Rrp must be positive'
              })
            }
            if (
              !zonePrice.grossProfitPercentage ||
              zonePrice.grossProfitPercentage <= 0
            ) {
              validationMessages.push({
                fieldName: 'grossProfitPercentage',
                message: 'GP% must be positive'
              })
            }
          }
        })
      }
    }

    return validationMessages
  },

  checkForDuplicateSpecial(specials, special, specialIndex) {
    return specials.reduce((acc, currSpecial, currSpecialIndex) => {
      if (currSpecialIndex !== specialIndex) {
        if (JSON.stringify(currSpecial) === JSON.stringify(special)) {
          return acc.concat(currSpecialIndex)
        }
      }
      return acc
    }, [])
  },

  checkForDuplicateSpecials(specials) {
    let duplicateSpecialIndexes = []
    specials.forEach((special, specialIndex) => {
      duplicateSpecialIndexes = duplicateSpecialIndexes.concat(
        MultiSpecialUploadHelper.checkForDuplicateSpecial(
          specials,
          special,
          specialIndex
        )
      )
    })
    return duplicateSpecialIndexes
  },

  isSpecialDataValid(special) {
    const validation = MultiSpecialUploadHelper.validateSpecialData(special)
    return validation.length === 0
  },

  isSpecialPricingValid(special) {
    const validation = MultiSpecialUploadHelper.validateSpecialPricing(special)
    return validation.length === 0
  },

  isRowDuplicate(specials, special, index) {
    return (
      MultiSpecialUploadHelper.checkForDuplicateSpecial(
        specials,
        special,
        index
      ).length > 0
    )
  },

  validateSpecials(specials) {
    for (const special of specials) {
      const validation = [
        ...MultiSpecialUploadHelper.validateSpecialData(special),
        ...MultiSpecialUploadHelper.validateSpecialPricing(special)
      ]

      if (validation.length > 0) {
        return false
      }
    }
    const duplicates = this.checkForDuplicateSpecials(specials)
    return duplicates.length <= 0
  }
}
